"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { useAppContext } from "../../../../shared/functions/Context"
import showModalFromId from "../../../../shared/functions/ModalShow"
import Modal from "../../../../shared/components/Modal"
import { CriteriaBox } from "../components/CriteriaBox"
import NewLeadershipModal from "../modals/CreateLeadershipModal"
import QuestionnairePreview from "../modals/QuestionnairePreview"
import { Plus, Crown, Activity, Target, TrendingUp } from "lucide-react"

const LeadershipView = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)

  const addNewLeadership = () => {
    showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_LEADERSHIP_MODAL)
  }

  const leadershipCriteria = store.leadership.all.map((item) => item.asJson)

  useEffect(() => {
    const load = async () => {
      try {
        await api.leadership.getAll()
      } catch (error) {
        console.error("Failed to fetch leadership criteria:", error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [api.leadership])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-purple-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 rounded-2xl p-6 border border-purple-100/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Leadership Criteria</h2>
            <p className="text-slate-600">Define leadership competencies and evaluation standards</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-2">
            <p className="text-slate-700">
              Establish clear leadership criteria to evaluate and develop leadership capabilities across your
              organization.
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-purple-600" />
                <span>{leadershipCriteria.length} Criteria Defined</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-600" />
                <span>Leadership Development</span>
              </div>
            </div>
          </div>

          <button
            onClick={addNewLeadership}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
          >
            <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
            Create Leadership Criteria
          </button>
        </div>
      </div>

      {/* Content Section */}
      {leadershipCriteria.length > 0 ? (
        <CriteriaBox value={leadershipCriteria} />
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-12 text-center">
          <div className="max-w-md mx-auto space-y-6">
            <div className="p-4 bg-purple-100 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
              <Crown className="h-10 w-10 text-purple-600" />
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-slate-800">No Leadership Criteria Defined</h3>
              <p className="text-slate-600 leading-relaxed">
                Define your first leadership criterion to start evaluating and developing leadership skills. Clear
                criteria help identify and nurture future leaders in your organization.
              </p>
            </div>
            <button
              onClick={addNewLeadership}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
              Create Your First Criterion
            </button>
          </div>
        </div>
      )}

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.CREATE_LEADERSHIP_MODAL}>
        <NewLeadershipModal />
      </Modal>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.QUESTIONNAIRE_PREVIEW}>
        <QuestionnairePreview />
      </Modal>
    </div>
  )
})

export default LeadershipView
